// Iter 31y — Pagina Statistiche.
// Due schede:
//   • "Per commessa" — selezionare una commessa e vedere riepilogo rapportini
//     + totali per dipendente + dettaglio per giorno.
//   • "Per dipendenti" — multi-select dipendenti, filtro commessa opzionale,
//     range date selezionabile, totali stampabili.
// Tutto a video, niente export — la stampa va via Ctrl+P (window.print).
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import CommessaCombobox from "@/components/CommessaCombobox";
import { StoricoAnnuale } from "@/components/StoricoAnnuale";
import {
  BarChart3,
  Users,
  Briefcase,
  Printer,
  Loader2,
  Search,
  CheckSquare,
  Square,
} from "lucide-react";

const _now = new Date();
const _pad2 = (n) => String(n).padStart(2, "0");
const fmtIso = (d) =>
  `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`;
const fmtIt = (iso) => {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function StatistichePage() {
  const { t } = useTranslation("app");
  const { user } = useAuth();
  const [tab, setTab] = useState("commessa");
  const [commesse, setCommesse] = useState([]);
  const [commesseStoriche, setCommesseStoriche] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Iter 54: tariffa oraria manodopera (solo admin)
  const [tariffa, setTariffa] = useState(null);

  // Tab "commessa"
  const [cmSel, setCmSel] = useState({ codice: "", id: null });
  const [cmDateFrom, setCmDateFrom] = useState("");
  const [cmDateTo, setCmDateTo] = useState("");
  const [cmOnlyVidim, setCmOnlyVidim] = useState(false);
  const [cmReport, setCmReport] = useState(null);
  const [cmLoading, setCmLoading] = useState(false);

  // Tab "dipendenti"
  const [empIds, setEmpIds] = useState(() => new Set());
  const [empSearch, setEmpSearch] = useState("");
  const [empCmFilter, setEmpCmFilter] = useState({ codice: "", id: null });
  const [empDateFrom, setEmpDateFrom] = useState("");
  const [empDateTo, setEmpDateTo] = useState("");
  const [empOnlyVidim, setEmpOnlyVidim] = useState(false);
  const [empReport, setEmpReport] = useState(null);
  const [empLoading, setEmpLoading] = useState(false);

  // Iter 38: domanda "includi anche la commessa madre/figlia?"
  const [sibAsk, setSibAsk] = useState(null); // { tab, sib, rel }

  // Iter 51: unisce le commesse dell'app con quelle presenti SOLO
  // nell'archivio ore storiche (import PDF gen-mag 2026), così sono
  // selezionabili nelle statistiche senza creare commesse fittizie.
  const mergedCommesse = useMemo(() => {
    const known = new Set(
      commesse.map((c) => (c.codice || "").toUpperCase())
    );
    const extra = commesseStoriche
      .filter((s) => !known.has((s.codice || "").toUpperCase()))
      .map((s) => ({
        id: `storico-${s.codice}`,
        codice: s.codice,
        descrizione:
          (s.descrizione ? `${s.descrizione} · ` : "") +
          "solo archivio storico",
        cliente: s.cliente || "",
      }));
    return [...commesse, ...extra];
  }, [commesse, commesseStoriche]);

  const findSibling = (codice) => {
    const c = (codice || "").trim().toUpperCase();
    if (!c) return null;
    const sibCode = c.endsWith("-OFF") ? c.slice(0, -4) : `${c}-OFF`;
    return (
      mergedCommesse.find(
        (x) => (x.codice || "").toUpperCase() === sibCode && x.is_deleted !== true
      ) || null
    );
  };

  // Init: carica commesse e dipendenti
  useEffect(() => {
    api.get("/commesse").then((r) => setCommesse(r.data || [])).catch(() => {});
    api
      .get("/statistiche/commesse-storiche")
      .then((r) => setCommesseStoriche(r.data || []))
      .catch(() => {});
    api
      .get("/employees")
      .then((r) => setEmployees(r.data || []))
      .catch(() => {});
    if (user?.role === "admin") {
      api
        .get("/settings")
        .then((r) => setTariffa(r.data?.costo_orario_default ?? 20))
        .catch(() => {});
    }
  }, []);

  // Carica statistiche commessa
  const loadCommessa = async (includeSibling = false) => {
    if (!cmSel.codice && !cmSel.id) {
      toast.error("Seleziona una commessa");
      return;
    }
    setCmLoading(true);
    try {
      const params = {};
      if (cmSel.id) params.commessa_id = cmSel.id;
      else params.commessa_codice = cmSel.codice;
      if (cmDateFrom) params.date_from = cmDateFrom;
      if (cmDateTo) params.date_to = cmDateTo;
      if (cmOnlyVidim) params.only_vidimati = true;
      if (includeSibling) params.include_sibling = true;
      const { data } = await api.get("/statistiche/commessa", { params });
      setCmReport(data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Caricamento statistiche non riuscito"
      );
    } finally {
      setCmLoading(false);
    }
  };

  // Carica statistiche dipendenti
  const loadDipendenti = async (includeSibling = false) => {
    if (empIds.size === 0) {
      toast.error("Seleziona almeno un dipendente");
      return;
    }
    setEmpLoading(true);
    try {
      const payload = {
        employee_ids: Array.from(empIds),
      };
      if (empCmFilter.id) payload.commessa_id = empCmFilter.id;
      else if (empCmFilter.codice) payload.commessa_codice = empCmFilter.codice;
      if (empDateFrom) payload.date_from = empDateFrom;
      if (empDateTo) payload.date_to = empDateTo;
      if (empOnlyVidim) payload.only_vidimati = true;
      if (includeSibling) payload.include_sibling = true;
      const { data } = await api.post("/statistiche/dipendenti", payload);
      setEmpReport(data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Caricamento statistiche non riuscito"
      );
    } finally {
      setEmpLoading(false);
    }
  };

  // Iter 38: chiedi se includere la commessa sorella prima di calcolare
  const askCommessa = () => {
    const sib = findSibling(cmSel.codice);
    if (sib) {
      const rel = (cmSel.codice || "").toUpperCase().endsWith("-OFF")
        ? "madre"
        : "figlia (officina)";
      setSibAsk({ tab: "commessa", sib, rel, sel: cmSel.codice });
    } else loadCommessa(false);
  };

  const askDipendenti = () => {
    const sib = empCmFilter.codice ? findSibling(empCmFilter.codice) : null;
    if (sib) {
      const rel = (empCmFilter.codice || "").toUpperCase().endsWith("-OFF")
        ? "madre"
        : "figlia (officina)";
      setSibAsk({ tab: "dipendenti", sib, rel, sel: empCmFilter.codice });
    } else loadDipendenti(false);
  };

  const answerSibAsk = (include) => {
    const t = sibAsk?.tab;
    setSibAsk(null);
    if (t === "commessa") loadCommessa(include);
    else if (t === "dipendenti") loadDipendenti(include);
  };

  // Filtra dipendenti per ricerca testuale
  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    const list = employees.filter(
      (e) => (e.attivo !== false) && (e.is_deleted !== true)
    );
    if (!q) return list;
    return list.filter((e) => {
      const full = `${e.cognome || ""} ${e.nome || ""}`.toLowerCase();
      return full.includes(q);
    });
  }, [employees, empSearch]);

  const toggleEmp = (id) => {
    setEmpIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const selectAllEmp = () => {
    setEmpIds(new Set(filteredEmployees.map((e) => e.id)));
  };
  const clearEmp = () => setEmpIds(new Set());

  // Solo admin/viewer (i capo cantiere non hanno menu Statistiche)
  if (user?.role === "capo_cantiere" || user?.role === "capo_officina") {
    return (
      <div className="p-8 text-center text-slate-500">
        {t("stats.onlyAdmins")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between print:hidden">
        <h1 className="text-3xl font-heading font-black tracking-tight flex items-center gap-3">
          <BarChart3 className="h-7 w-7 text-[#0044FF]" />
          {t("stats.title")}
        </h1>
        <Button
          variant="outline"
          onClick={() => window.print()}
          disabled={!cmReport && !empReport}
          data-testid="statistiche-print"
        >
          <Printer className="h-4 w-4 mr-2" /> {t("stats.print")}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="print:hidden max-w-full flex-wrap h-auto justify-start">
          <TabsTrigger value="commessa" data-testid="tab-commessa">
            <Briefcase className="h-4 w-4 mr-1.5" /> {t("stats.perCommessa")}
          </TabsTrigger>
          <TabsTrigger value="dipendenti" data-testid="tab-dipendenti">
            <Users className="h-4 w-4 mr-1.5" /> {t("stats.perDipendenti")}
          </TabsTrigger>
          <TabsTrigger value="storico" data-testid="tab-storico">
            <BarChart3 className="h-4 w-4 mr-1.5" /> {t("stats.storicoTab")}
          </TabsTrigger>
        </TabsList>

        {/* ============ TAB STORICO 2021-2025 (Iter 74) ============ */}
        <TabsContent value="storico" className="space-y-4">
          <StoricoAnnuale />
        </TabsContent>

        {/* ============ TAB COMMESSA ============ */}
        <TabsContent value="commessa" className="space-y-4">
          <Card className="p-4 md:p-5 print:hidden">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-5">
                <Label className="text-xs font-bold">{t("rapp.commessa")}</Label>
                <CommessaCombobox
                  testId="cm-selector"
                  commesse={mergedCommesse}
                  value={cmSel.codice}
                  onSelect={(c) => {
                    const isStorico = String(c.id || "").startsWith("storico-");
                    setCmSel({
                      codice: c.codice || "",
                      id: isStorico ? null : c.id || null,
                    });
                  }}
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs font-bold">{t("riep.dal")}</Label>
                <input
                  type="date"
                  value={cmDateFrom}
                  onChange={(e) => setCmDateFrom(e.target.value)}
                  className="w-full mt-1 h-11 rounded-md border border-slate-300 px-3"
                  data-testid="cm-date-from"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs font-bold">{t("riep.al")}</Label>
                <input
                  type="date"
                  value={cmDateTo}
                  onChange={(e) => setCmDateTo(e.target.value)}
                  className="w-full mt-1 h-11 rounded-md border border-slate-300 px-3"
                  data-testid="cm-date-to"
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  onClick={askCommessa}
                  disabled={cmLoading || (!cmSel.codice && !cmSel.id)}
                  className="w-full h-11 bg-[#0044FF] hover:bg-[#0033CC]"
                  data-testid="cm-load"
                >
                  {cmLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <label className="text-xs text-slate-600 flex items-center gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cmOnlyVidim}
                onChange={(e) => setCmOnlyVidim(e.target.checked)}
                data-testid="cm-only-vidim"
              />
              {t("stats.onlyVidim")}
            </label>
          </Card>

          {cmReport && <ReportCommessa report={cmReport} tariffa={tariffa} />}
        </TabsContent>

        {/* ============ TAB DIPENDENTI ============ */}
        <TabsContent value="dipendenti" className="space-y-4">
          <Card className="p-4 md:p-5 print:hidden">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
              <div className="md:col-span-5">
                <Label className="text-xs font-bold">
                  {t("stats.commessaOpt")}
                </Label>
                <CommessaCombobox
                  testId="emp-cm-filter"
                  commesse={commesse}
                  value={empCmFilter.codice}
                  onSelect={(c) =>
                    setEmpCmFilter({
                      codice: c.codice || "",
                      id: c.id || null,
                    })
                  }
                />
                {(empCmFilter.codice || empCmFilter.id) && (
                  <button
                    type="button"
                    onClick={() =>
                      setEmpCmFilter({ codice: "", id: null })
                    }
                    className="text-[11px] underline text-slate-600 mt-1"
                    data-testid="emp-cm-clear"
                  >
                    {t("stats.removeFilter")}
                  </button>
                )}
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs font-bold">{t("riep.dal")}</Label>
                <input
                  type="date"
                  value={empDateFrom}
                  onChange={(e) => setEmpDateFrom(e.target.value)}
                  className="w-full mt-1 h-11 rounded-md border border-slate-300 px-3"
                  data-testid="emp-date-from"
                />
              </div>
              <div className="md:col-span-3">
                <Label className="text-xs font-bold">{t("riep.al")}</Label>
                <input
                  type="date"
                  value={empDateTo}
                  onChange={(e) => setEmpDateTo(e.target.value)}
                  className="w-full mt-1 h-11 rounded-md border border-slate-300 px-3"
                  data-testid="emp-date-to"
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  onClick={askDipendenti}
                  disabled={empLoading || empIds.size === 0}
                  className="w-full h-11 bg-[#0044FF] hover:bg-[#0033CC]"
                  data-testid="emp-load"
                >
                  {empLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <label className="text-xs text-slate-600 flex items-center gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={empOnlyVidim}
                onChange={(e) => setEmpOnlyVidim(e.target.checked)}
                data-testid="emp-only-vidim"
              />
              Solo rapportini vidimati
            </label>
          </Card>

          {/* Selettore dipendenti */}
          <Card className="p-4 md:p-5 print:hidden">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="font-heading font-bold flex items-center gap-2">
                <Users className="h-4 w-4" />
                {t("stats.selEmp")} ({empIds.size} {t("stats.of")}{" "}
                {filteredEmployees.length})
              </div>
              <div className="flex gap-2">
                <Input
                  value={empSearch}
                  onChange={(e) => setEmpSearch(e.target.value)}
                  placeholder={t("stats.searchEmp")}
                  className="h-9 w-56"
                  data-testid="emp-search"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={selectAllEmp}
                  data-testid="emp-select-all"
                >
                  <CheckSquare className="h-4 w-4 mr-1" /> {t("stats.all")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={clearEmp}
                  disabled={empIds.size === 0}
                  data-testid="emp-clear"
                >
                  <Square className="h-4 w-4 mr-1" /> {t("stats.none")}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5 max-h-[400px] overflow-y-auto">
              {filteredEmployees.map((e) => {
                const checked = empIds.has(e.id);
                return (
                  <label
                    key={e.id}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors ${
                      checked
                        ? "bg-blue-50 border border-blue-300"
                        : "border border-transparent hover:bg-slate-50"
                    }`}
                    data-testid={`emp-row-${e.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleEmp(e.id)}
                      className="h-4 w-4"
                    />
                    <span className="truncate">
                      {(e.cognome || "").trim()} {(e.nome || "").trim()}
                    </span>
                  </label>
                );
              })}
              {filteredEmployees.length === 0 && (
                <div className="col-span-full text-sm text-slate-500 italic">
                  {t("stats.noEmpFound")}
                </div>
              )}
            </div>
          </Card>

          {empReport && <ReportDipendenti report={empReport} />}
        </TabsContent>
      </Tabs>

      {/* Iter 38: dialog "includi commessa madre/figlia?" */}
      <Dialog open={!!sibAsk} onOpenChange={(o) => !o && setSibAsk(null)}>
        <DialogContent data-testid="sibling-ask-dialog">
          <DialogHeader>
            <DialogTitle>Includere anche la commessa {sibAsk?.rel}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            La commessa <strong className="font-mono">{sibAsk?.sel}</strong> ha
            una commessa {sibAsk?.rel}{" "}
            <strong className="font-mono">{sibAsk?.sib?.codice}</strong>.
            Vuoi considerare anche le sue ore nelle statistiche?
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => answerSibAsk(false)}
              data-testid="sibling-ask-no"
            >
              No, solo questa
            </Button>
            <Button
              className="bg-[#0044FF] hover:bg-[#0033CC]"
              onClick={() => answerSibAsk(true)}
              data-testid="sibling-ask-yes"
            >
              Sì, includi {sibAsk?.sib?.codice}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----------------------- COMPONENTI RIEPILOGO -----------------------

function ReportCommessa({ report, tariffa }) {
  const { t } = useTranslation("app");
  const { commessa, totals, by_employee, detail, n_rapportini, n_giorni } =
    report;
  return (
    <div className="space-y-4">
      <Card className="p-4 md:p-5 bg-blue-50 border-blue-200 print:bg-white print:border-slate-300">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-mono font-bold text-2xl text-[#0044FF]">
            {commessa.codice}
          </span>
          {commessa.cliente && (
            <span className="text-sm text-slate-700">· {commessa.cliente}</span>
          )}
          {report.sibling && (
            <span
              className="text-xs font-mono font-bold bg-blue-100 text-blue-800 rounded px-2 py-0.5"
              data-testid="report-sibling-badge"
            >
              + {report.sibling.codice}
            </span>
          )}
        </div>
        {commessa.descrizione && (
          <div className="text-sm text-slate-600 italic mb-3">
            {commessa.descrizione}
          </div>
        )}
        <div
          className={`grid grid-cols-2 gap-3 text-sm ${
            tariffa != null ? "md:grid-cols-5" : "md:grid-cols-4"
          }`}
        >
          <Stat label={t("stats.rapportini")} value={n_rapportini} />
          <Stat label={t("stats.giorni")} value={n_giorni} />
          <Stat label={t("stats.dipendenti")} value={by_employee.length} />
          <Stat
            label={t("stats.oreTotali")}
            value={(report.totale_complessivo ?? totals.ore_tot).toFixed(2)}
            highlight
          />
          {tariffa != null && (
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50 p-3"
              data-testid="stat-costo-manodopera"
              title={`${(report.totale_complessivo ?? totals.ore_tot).toFixed(2)} h × ${tariffa} €/h (tariffa in Impostazioni). Solo manodopera: NON include materiali e altri costi.`}
            >
              <div className="text-[11px] uppercase tracking-wider text-emerald-700">
                {t("stats.costoManodopera")}
              </div>
              <div className="font-mono font-bold text-lg text-emerald-800">
                {(
                  (report.totale_complessivo ?? totals.ore_tot) * tariffa
                ).toLocaleString("it-IT", {
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 0,
                })}
              </div>
              <div className="text-[10px] text-emerald-600">
                {tariffa} €/h · {t("stats.esclusi")}
              </div>
            </div>
          )}
        </div>
        {report.storico && report.storico.ore_tot > 0 && (
          <div
            className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 print:border-slate-300"
            data-testid="report-storico-note"
          >
            <span className="font-bold">
              di cui archivio storico: {report.storico.ore_tot.toFixed(2)} h
            </span>{" "}
            <span className="text-xs">
              (periodo {fmtIt(report.storico.periodo_from)} →{" "}
              {fmtIt(report.storico.periodo_to)} · L{" "}
              {report.storico.ore_l.toFixed(2)} · V{" "}
              {report.storico.ore_v.toFixed(2)} · G{" "}
              {report.storico.ore_g.toFixed(2)})
            </span>
            <div className="text-[11px] text-amber-700 mt-0.5">
              Ore importate dal prospetto pre-app (gen–mag 2026): incluse nel
              totale, non presenti nei rapportini.
              {report.storico_only &&
                " Questa commessa esiste solo nell'archivio storico."}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-900 text-white font-heading font-bold">
          {t("stats.totPerDip")}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr className="text-[11px] uppercase tracking-wider">
              <th className="px-3 py-2 text-left">{t("riep.tab.dipendente")}</th>
              <th className="px-2 py-2 text-center">{t("stats.giorni")}</th>
              <th className="px-2 py-2 text-center">{t("rapp.lavoro")}</th>
              <th className="px-2 py-2 text-center">{t("stats.caldo")}</th>
              <th className="px-2 py-2 text-center">{t("stats.notturne")}</th>
              <th className="px-2 py-2 text-center">{t("rapp.guida")}</th>
              <th className="px-2 py-2 text-center">{t("rapp.passegg")}</th>
              <th className="px-2 py-2 text-center font-bold">{t("stats.totale")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {by_employee.map((emp) => (
              <tr
                key={emp.employee_id || emp.employee_nome}
                data-testid={`cm-emp-row-${emp.employee_id || emp.employee_nome}`}
              >
                <td className="px-3 py-2">{emp.employee_nome}</td>
                <td className="px-2 py-2 text-center font-mono">
                  {emp.n_giorni}
                </td>
                <td className="px-2 py-2 text-center font-mono">
                  {emp.ore_lavoro.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono text-orange-700">
                  {emp.ore_caldo.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono text-purple-700">
                  {emp.ore_notturne.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono">
                  {emp.ore_guida.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono">
                  {emp.ore_passegg.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono font-bold text-base">
                  {emp.ore_tot.toFixed(2)}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-900 text-white font-mono font-bold">
              <td className="px-3 py-2 uppercase tracking-wider text-xs">
                {t("stats.totCommessa")}
              </td>
              <td></td>
              <td className="px-2 py-2 text-center">
                {totals.ore_lavoro.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center">
                {totals.ore_caldo.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center">
                {totals.ore_notturne.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center">
                {totals.ore_guida.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center">
                {totals.ore_passegg.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center text-base">
                {totals.ore_tot.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-900 text-white font-heading font-bold">
          {t("stats.dettaglio")}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-100">
              <tr className="uppercase tracking-wider">
                <th className="px-2 py-2 text-left">{t("stats.data")}</th>
                <th className="px-2 py-2 text-left">Rapp.</th>
                <th className="px-2 py-2 text-left">{t("riep.tab.dipendente")}</th>
                <th className="px-2 py-2 text-center">{t("rapp.lavoro")}</th>
                <th className="px-2 py-2 text-center">{t("stats.caldo")}</th>
                <th className="px-2 py-2 text-center">Nott.</th>
                <th className="px-2 py-2 text-center">{t("rapp.guida")}</th>
                <th className="px-2 py-2 text-center">Pass.</th>
                <th className="px-2 py-2 text-center font-bold">{t("rapp.tot")}</th>
                <th className="px-2 py-2 text-left">{t("stats.note")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
                {detail.map((d, i) => (
                <tr key={`${d.rapportino_numero}-${d.data}-${i}`} className="hover:bg-slate-50/60">
                  <td className="px-2 py-1.5 font-mono">{d.data}</td>
                  <td className="px-2 py-1.5 font-mono">
                    #{d.rapportino_numero}
                    {d.vidimato ? "" : "*"}
                  </td>
                  <td className="px-2 py-1.5">{d.employee_nome}</td>
                  <td className="px-2 py-1.5 text-center font-mono">
                    {d.ore_lavoro || ""}
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono text-orange-700">
                    {d.ore_caldo || ""}
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono text-purple-700">
                    {d.ore_notturne || ""}
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono">
                    {d.ore_guida || ""}
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono">
                    {d.ore_passegg || ""}
                  </td>
                  <td className="px-2 py-1.5 text-center font-mono font-bold">
                    {d.ore_tot.toFixed(2)}
                  </td>
                  <td className="px-2 py-1.5 text-slate-600 italic">
                    {d.note}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ReportDipendenti({ report }) {
  const { t } = useTranslation("app");
  const { per_dipendente, totals, detail, filters } = report;
  return (
    <div className="space-y-4">
      <Card className="p-4 md:p-5 bg-purple-50 border-purple-200 print:bg-white">
        <div className="font-heading font-bold mb-2 text-lg">
          {t("stats.selectedSummary", { count: per_dipendente.length })}
        </div>
        {(filters.date_from || filters.date_to) && (
          <div className="text-xs text-slate-700 mb-2">
            {t("stats.periodo")}: <strong>{filters.date_from || t("stats.inizio")}</strong> →{" "}
            <strong>{filters.date_to || t("stats.oggi")}</strong>
          </div>
        )}
        {(filters.commessa_id || filters.commessa_codice) && (
          <div className="text-xs text-slate-700 mb-2">
            {t("stats.filtroCommessa")}{" "}
            <strong className="font-mono">
              {filters.commessa_codice || filters.commessa_id}
            </strong>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mt-3">
          <Stat
            label={t("stats.oreTotali")}
            value={totals.ore_tot.toFixed(2)}
            highlight
          />
          <Stat label={t("rapp.lavoro")} value={totals.ore_lavoro.toFixed(2)} />
          <Stat label={t("stats.caldo")} value={totals.ore_caldo.toFixed(2)} />
          <Stat label={t("stats.notturne")} value={totals.ore_notturne.toFixed(2)} />
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-900 text-white font-heading font-bold">
          {t("stats.totPerDip2")}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100">
            <tr className="text-[11px] uppercase tracking-wider">
              <th className="px-3 py-2 text-left">{t("riep.tab.dipendente")}</th>
              <th className="px-2 py-2 text-center">{t("stats.giorni")}</th>
              <th className="px-2 py-2 text-center">{t("rapp.lavoro")}</th>
              <th className="px-2 py-2 text-center">{t("stats.caldo")}</th>
              <th className="px-2 py-2 text-center">{t("stats.notturne")}</th>
              <th className="px-2 py-2 text-center">{t("rapp.guida")}</th>
              <th className="px-2 py-2 text-center">{t("rapp.passegg")}</th>
              <th className="px-2 py-2 text-center font-bold">{t("stats.totale")}</th>
              <th className="px-2 py-2 text-left">{t("stats.commesse")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {per_dipendente.map((emp) => (
              <tr
                key={emp.employee_id}
                data-testid={`emp-totals-row-${emp.employee_id}`}
                className={
                  emp.ore_tot === 0 ? "text-slate-400 italic" : ""
                }
              >
                <td className="px-3 py-2">{emp.employee_nome}</td>
                <td className="px-2 py-2 text-center font-mono">
                  {emp.n_giorni}
                </td>
                <td className="px-2 py-2 text-center font-mono">
                  {emp.ore_lavoro.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono text-orange-700">
                  {emp.ore_caldo.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono text-purple-700">
                  {emp.ore_notturne.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono">
                  {emp.ore_guida.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono">
                  {emp.ore_passegg.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-center font-mono font-bold text-base">
                  {emp.ore_tot.toFixed(2)}
                </td>
                <td className="px-2 py-2 text-[10px] text-slate-600 font-mono">
                  {emp.commesse.join(", ") || "—"}
                </td>
              </tr>
            ))}
            <tr className="bg-slate-900 text-white font-mono font-bold">
              <td className="px-3 py-2 uppercase tracking-wider text-xs">
                {t("stats.totale")}
              </td>
              <td></td>
              <td className="px-2 py-2 text-center">
                {totals.ore_lavoro.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center">
                {totals.ore_caldo.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center">
                {totals.ore_notturne.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center">
                {totals.ore_guida.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center">
                {totals.ore_passegg.toFixed(2)}
              </td>
              <td className="px-2 py-2 text-center text-base">
                {totals.ore_tot.toFixed(2)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </Card>

      {detail.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-900 text-white font-heading font-bold">
            {t("stats.dettaglio")} ({detail.length})
          </div>
          <div className="overflow-x-auto max-h-[600px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0">
                <tr className="uppercase tracking-wider">
                  <th className="px-2 py-2 text-left">{t("stats.data")}</th>
                  <th className="px-2 py-2 text-left">Rapp.</th>
                  <th className="px-2 py-2 text-left">{t("riep.tab.dipendente")}</th>
                  <th className="px-2 py-2 text-left">{t("rapp.commessa")}</th>
                  <th className="px-2 py-2 text-center font-bold">{t("rapp.tot")}</th>
                  <th className="px-2 py-2 text-left">{t("stats.note")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {detail.map((d, i) => (
                  <tr key={`${d.rapportino_numero}-${d.data}-${i}`}>
                    <td className="px-2 py-1.5 font-mono">{d.data}</td>
                    <td className="px-2 py-1.5 font-mono">
                      #{d.rapportino_numero}
                      {d.vidimato ? "" : "*"}
                    </td>
                    <td className="px-2 py-1.5">{d.employee_nome}</td>
                    <td className="px-2 py-1.5 font-mono">
                      {d.commessa_codice}
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono font-bold">
                      {d.ore_tot.toFixed(2)}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600 italic">
                      {d.note}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div
      className={`rounded-md px-3 py-2 ${
        highlight
          ? "bg-[#0044FF] text-white"
          : "bg-white border border-slate-200"
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider opacity-70">
        {label}
      </div>
      <div className="font-mono font-bold text-lg">{value}</div>
    </div>
  );
}
